import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileExcelOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  InputNumber,
  Row,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { errorText, formatAmount, saveBlob } from '../format';

const { Text } = Typography;

const codeColumns = (t) => [
  { title: t('bsMigration.bsCode'), dataIndex: 'analytic', key: 'analytic', width: 100 },
  {
    title: t('ab.name'),
    dataIndex: 'name',
    key: 'name',
    ellipsis: { showTitle: true },
  },
  { title: t('bsMigration.abCode'), dataIndex: 'abCode', key: 'abCode', width: 120 },
];

const yes = (t) => (flag) => (flag ? t('bsMigration.yes') : '');

/**
 * The company's AB opening balance as an Excel workbook to import into
 * BizniSoft: accounts, partners and banks renumbered to BizniSoft codes, the
 * order's lines on the switch-over date and the fixed assets. Read-only; the
 * file is made on request.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @returns {JSX.Element} The migration tab
 */
export default function BsMigrationTab({ companyId }) {
  const { t } = useTranslation('clientReports');
  const [asOf, setAsOf] = useState(dayjs().startOf('year'));
  const year = asOf.year();
  const [partnerStart, setPartnerStart] = useState(1);
  const [bankStart, setBankStart] = useState(1);

  const params = {
    asOf: asOf.format('YYYY-MM-DD'),
    partnerStart: partnerStart || 1,
    bankStart: bankStart || 1,
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-bs-migration', companyId, params],
    queryFn: () => API.clientReports.getBsMigration(companyId, params),
    retry: false,
  });
  const result = data?.data;

  const exportMutation = useMutation({
    mutationFn: () => API.clientReports.exportBsMigration(companyId, params),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const fromJanFirst = asOf.month() === 0 && asOf.date() === 1;
  const notFound = fromJanFirst
    ? t('bsMigration.noOpening', { year })
    : t('bsMigration.noLedger', { date: asOf.format('DD.MM.YYYY') });
  const errorMessage =
    error?.response?.status === 404
      ? notFound
      : error?.response?.data?.message || error?.message;

  const balanced =
    result && Math.abs(result.totals.debit - result.totals.credit) < 0.01;

  return (
    <div>
      <Space wrap align="center" style={{ display: 'flex', marginBottom: 12 }}>
        <Space size={6}>
          <Text>{t('bsMigration.asOf')}</Text>
          <DatePicker
            allowClear={false}
            format="DD.MM.YYYY"
            value={asOf}
            onChange={(value) => value && setAsOf(value.startOf('day'))}
          />
        </Space>
        <Space size={6}>
          <Text>{t('bsMigration.partnerStart')}</Text>
          <InputNumber
            min={1}
            max={99999}
            precision={0}
            value={partnerStart}
            onChange={setPartnerStart}
          />
        </Space>
        <Space size={6}>
          <Text>{t('bsMigration.bankStart')}</Text>
          <InputNumber
            min={1}
            max={99999}
            precision={0}
            value={bankStart}
            onChange={setBankStart}
          />
        </Space>
        <Button
          shape="round"
          icon={<FileExcelOutlined />}
          disabled={!result}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('ab.exportExcel')}
        </Button>
      </Space>

      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('bsMigration.hint')}
      </Text>

      {errorMessage && (
        <Alert
          type="warning"
          showIcon
          message={errorMessage}
          style={{ marginBottom: 12 }}
        />
      )}

      {result && (
        <>
          <Space wrap size="large" style={{ display: 'flex', marginBottom: 8 }}>
            <Text>
              {t('bsMigration.order')}:{' '}
              <Text strong>
                {result.asOf
                  ? t('bsMigration.ledgerTo', { date: asOf.format('DD.MM.YYYY') })
                  : result.orderNo}
              </Text>
            </Text>
            <Text>{t('bsMigration.lines', { count: result.lineCount })}</Text>
            {result.assetCount > 0 && (
              <Text>{t('bsMigration.assets', { count: result.assetCount })}</Text>
            )}
            <Text>
              {t('ab.debit')}:{' '}
              <Text strong>{formatAmount(result.totals.debit)}</Text>
            </Text>
            <Text type={balanced ? undefined : 'danger'}>
              {t('ab.credit')}:{' '}
              <Text strong>{formatAmount(result.totals.credit)}</Text>
            </Text>
          </Space>
          {result.migrationAt && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {t('bsMigration.sheets', result.counts)}
            </Text>
          )}

          {result.warnings.map((w) => (
            <Alert
              key={w.type}
              type={
                w.type === 'unbalanced'
                  ? 'error'
                  : w.type.startsWith('asset') ||
                      w.type === 'noMigration' ||
                      w.type === 'vatDeductibleDiff'
                    ? 'warning'
                    : 'info'
              }
              showIcon
              style={{ marginBottom: 8 }}
              message={t(`bsMigration.warning.${w.type}`, {
                count: w.items.length,
                value: w.items.join(', '),
                first: formatAmount(Number(w.items[0])),
                second: formatAmount(Number(w.items[1])),
              })}
              description={
                !['unbalanced', 'assetCost', 'assetDepreciation', 'vatDeductibleDiff'].includes(
                  w.type
                ) &&
                w.items.length
                  ? w.items.join(', ')
                  : undefined
              }
            />
          ))}

          <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
            <Col xs={24} xl={12}>
              <Card
                size="small"
                title={t('bsMigration.partners', {
                  count: result.partners.length,
                })}
              >
                <Table
                  rowKey="abCode"
                  size="small"
                  tableLayout="fixed"
                  pagination={false}
                  scroll={{ y: 360 }}
                  loading={isLoading}
                  dataSource={result.partners}
                  columns={codeColumns(t)}
                />
              </Card>
            </Col>
            <Col xs={24} xl={12}>
              <Card
                size="small"
                title={t('bsMigration.banks', { count: result.banks.length })}
              >
                <Table
                  rowKey="abCode"
                  size="small"
                  tableLayout="fixed"
                  pagination={false}
                  scroll={{ y: 360 }}
                  dataSource={result.banks}
                  columns={codeColumns(t)}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Card
                size="small"
                title={t('bsMigration.accounts', {
                  count: result.accounts.length,
                })}
              >
                <Table
                  rowKey="account"
                  size="small"
                  tableLayout="fixed"
                  pagination={false}
                  scroll={{ y: 360 }}
                  dataSource={result.accounts}
                  columns={[
                    { title: t('ab.account'), dataIndex: 'account', key: 'account', width: 100 },
                    {
                      title: t('ab.name'),
                      dataIndex: 'name',
                      key: 'name',
                      ellipsis: { showTitle: true },
                    },
                    {
                      title: t('bsMigration.analytic'),
                      dataIndex: 'analytic',
                      key: 'analytic',
                      width: 110,
                    },
                    {
                      title: t('bsMigration.foreign'),
                      dataIndex: 'foreign',
                      key: 'foreign',
                      width: 90,
                      render: yes(t),
                    },
                    {
                      title: t('bsMigration.openItems'),
                      dataIndex: 'openItems',
                      key: 'openItems',
                      width: 150,
                      render: yes(t),
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
